import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

// 导入语言资源
import zhCommon from "@/locales/zh/common.json";
import enCommon from "@/locales/en/common.json";
import jpCommon from "@/locales/jp/common.json";
import zhAdmin from "@/locales/zh/admin.json";
import enAdmin from "@/locales/en/admin.json";
import jpAdmin from "@/locales/jp/admin.json";
import zhLogin from "@/locales/zh/login.json";
import enLogin from "@/locales/en/login.json";
import jpLogin from "@/locales/jp/login.json";
import zhRegister from "@/locales/zh/register.json";
import enRegister from "@/locales/en/register.json";
import jpRegister from "@/locales/jp/register.json";
import zhAssessments from "@/locales/zh/assessments.json";
import enAssessments from "@/locales/en/assessments.json";
import jpAssessments from "@/locales/jp/assessments.json";
import zhSecurity from "@/locales/zh/security.json";
import enSecurity from "@/locales/en/security.json";
import jpSecurity from "@/locales/jp/security.json";
import zhTanks from "@/locales/zh/tanks.json";
import enTanks from "@/locales/en/tanks.json";
import jpTanks from "@/locales/jp/tanks.json";
import zhConsent from "@/locales/zh/consent.json";
import enConsent from "@/locales/en/consent.json";
import jpConsent from "@/locales/jp/consent.json";
import zhHome from "@/locales/zh/home.json";
import enHome from "@/locales/en/home.json";
import jpHome from "@/locales/jp/home.json";
import zhWaitlist from "@/locales/zh/waitlist.json";
import enWaitlist from "@/locales/en/waitlist.json";
import jpWaitlist from "@/locales/jp/waitlist.json";

// 支持的语言列表
export const SUPPORTED_LANGUAGES = ["zh", "en", "jp"] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

// 语言配置
export const LANGUAGE_CONFIG = {
  zh: {
    label: "简体中文",
    flag: "🇨🇳",
  },
  en: {
    label: "English",
    flag: "🇺🇸",
  },
  jp: {
    label: "日本語",
    flag: "🇯🇵",
  },
} as const;

// 默认语言
export const DEFAULT_LANGUAGE: SupportedLanguage = "en";

// 语言资源
const resources = {
  zh: {
    common: zhCommon,
    admin: zhAdmin,
    login: zhLogin,
    register: zhRegister,
    assessments: zhAssessments,
    security: zhSecurity,
    tanks: zhTanks,
    consent: zhConsent,
    home: zhHome,
    waitlist: zhWaitlist,
  },
  en: {
    common: enCommon,
    admin: enAdmin,
    login: enLogin,
    register: enRegister,
    assessments: enAssessments,
    security: enSecurity,
    tanks: enTanks,
    consent: enConsent,
    home: enHome,
    waitlist: enWaitlist,
  },
  jp: {
    common: jpCommon,
    admin: jpAdmin,
    login: jpLogin,
    register: jpRegister,
    assessments: jpAssessments,
    security: jpSecurity,
    tanks: jpTanks,
    consent: jpConsent,
    home: jpHome,
    waitlist: jpWaitlist,
  },
};

// 初始化 i18next
i18n
  // 检测用户语言
  .use(LanguageDetector)
  // 传递 i18n 实例给 react-i18next
  .use(initReactI18next)
  // 初始化 i18next
  .init({
    resources,
    fallbackLng: DEFAULT_LANGUAGE,
    defaultNS: "common",
    ns: [
      "common",
      "admin",
      "login",
      "register",
      "assessments",
      "security",
      "tanks",
      "consent",
      "home",
      "waitlist",
    ],

    // 语言检测顺序
    detection: {
      // 检测顺序: localStorage > navigator
      // 只有用户明确选择过语言才使用缓存，否则使用默认语言
      order: ["localStorage", "navigator"],
      // localStorage 键名：根据路径区分前后台，避免互相影响
      lookupLocalStorage: window.location.pathname.startsWith("/admin")
        ? "admin_i18nextLng"
        : "i18nextLng",
      // 缓存用户语言（只使用 localStorage，避免 cookie 冲突）
      caches: ["localStorage"],
      // 排除的语言
      excludeCacheFor: ["cimode"],
    },

    interpolation: {
      escapeValue: false, // React 已经处理了 XSS
    },

    // 开发环境下显示调试信息
    debug: import.meta.env.DEV,

    // 只加载支持的语言
    supportedLngs: SUPPORTED_LANGUAGES,

    // 非严格模式，允许回退到默认语言
    nonExplicitSupportedLngs: true,
  });

export default i18n;
